import { Component, Inject, OnInit } from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { XpoAgGridColumns } from '@xpo-ltl/ngx-ag-grid';
import { XpoAgGridBoardApi, XpoAgGridBoardReadyEvent, XpoAgGridBoardViewTemplate } from '@xpo-ltl/ngx-board/ag-grid';
import {
  XpoBoardOptions,
  XpoBoardState,
  XpoBoardViewConfig,
  XpoLocalStorageBoardViewDataStore,
} from '@xpo-ltl/ngx-board/core';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { ReplaySubject } from 'rxjs';
import { LinkCellRendererComponent } from 'src/app/location-details-page/company-hierarchy/components/link-cell-renderer/link-cell-renderer.component';
import { ServiceRecipientSearchService } from 'src/app/service-recipient-page/services/service-recipient-search.service';
import { AppRoutes } from '../../enums/app-routes.enum';
import { GridApiContextService } from '../../services/grid-api-context/grid-api-context.service';
import { SelectFilterComponent } from '../select-filter/select-filter.component';
import { TextFilterComponent } from '../text-filter/text-filter.component';
import { ServiceRecipientResultDialogService } from './services/service-recipient-result-dialog.service';

@Component({
  selector: 'app-service-recipient-result-dialog',
  providers: [ServiceRecipientResultDialogService, ServiceRecipientSearchService, GridApiContextService],
  templateUrl: './service-recipient-result-dialog.component.html',
  styleUrls: ['./service-recipient-result-dialog.component.scss'],
})
export class ServiceRecipientResultDialogComponent implements OnInit {
  customGridOptions: GridOptions;
  boardOptions: XpoBoardOptions;
  gridApi: GridApi;
  colApi: ColumnApi;
  gridBoardApi: XpoAgGridBoardApi;
  rowSelected = '';
  stateChange$ = new ReplaySubject<XpoBoardState>(1);
  search = '';

  readonly viewDataStore: XpoLocalStorageBoardViewDataStore;
  readonly viewTemplates: XpoAgGridBoardViewTemplate[];

  constructor(
    private dialog: MatDialogRef<ServiceRecipientResultDialogComponent>,
    public dataSource: ServiceRecipientResultDialogService,
    private gridApiContextService: GridApiContextService,
    @Inject(MAT_DIALOG_DATA) private data: any
  ) {
    this.viewTemplates = this.getBoardViewTemplates();
    this.viewDataStore = new XpoLocalStorageBoardViewDataStore('client-side-recipient-dialog', this.getBoardViews());
    this.search = this.data.search;
    this.dataSource.setSearch(this.search);
  }

  ngOnInit(): void {
    this.boardOptions = {
      addNewViewButtonLabel: '',
      suppressViewSwitcher: true,
      enableFilterReset: false,
      enableQueryParamStatePersistance: true,
      persistFiltersBetweenViews: false,
      preloadViewData: true,
      suppressGridDensity: true,
      suppressGridSettingsPopover: true,
      suppressRecordCounts: false,
    };

    this.customGridOptions = {
      suppressRowClickSelection: true,
      localeText: { noRowsToShow: 'No Service Recipients were found for your search criteria.' },
      frameworkComponents: {
        linkRenderer: LinkCellRendererComponent,
        selectFilterComponent: SelectFilterComponent,
        textFilterComponent: TextFilterComponent,
      },
      animateRows: true,
      pagination: true,
      defaultColDef: {
        suppressMenu: true,
      },
      domLayout: 'autoHeight',
      suppressPaginationPanel: true,
      context: { filtersOptionsVisibleDefaultValue: true },
    };
  }

  refreshGrid(): void {
    this.dataSource.refresh();
  }

  saveRecipient(): void {
    this.dialog.close(this.rowSelected);
  }

  close(): void {
    this.dialog.close();
  }

  gridBoardReady(gridEvent: XpoAgGridBoardReadyEvent): void {
    this.gridApi = gridEvent.agGridApi;
    this.gridApiContextService.saveGridApi(this.gridApi);
    this.colApi = gridEvent.agGridColumnApi;
    this.gridBoardApi = gridEvent.agGridBoardApi;
  }

  onSelectionChange(event): void {
    this.rowSelected = event[0];
  }

  private getBoardViews(): XpoBoardViewConfig[] {
    return [
      {
        templateId: 'template1',
        name: 'All - SD',
        closeable: false,
      },
    ];
  }

  private getBoardViewTemplates(): XpoAgGridBoardViewTemplate[] {
    return [
      new XpoAgGridBoardViewTemplate({
        id: 'template1',
        name: 'Template 1',
        allowAdditional: true,
        availableColumns: [
          XpoAgGridColumns.RowIndex,
          XpoAgGridColumns.Selection,
          {
            headerName: 'Number',
            field: 'serviceRecipientNbr',
            colId: 'serviceRecipientNbr',
            minWidth: 150,
            sortable: false,
            cellRenderer: 'linkRenderer',
            cellRendererParams: ({ data }) => {
              return {
                url: data && `${AppRoutes.SERVICE_RECIPIENT_PAGE}/profile/` + data.serviceRecipientNbr,
                text: data && data.serviceRecipientNbr,
              };
            },
          },
          {
            headerName: 'Name',
            field: 'serviceRecipientName',
            colId: 'serviceRecipientName',
            minWidth: 290,
            resizable: true,
          },
          {
            headerName: 'Type',
            field: 'typeOfServiceInd',
            colId: 'typeOfServiceInd',
            minWidth: 100,
          },
          {
            headerName: 'Address',
            field: 'address',
            colId: 'address',
            minWidth: 250,
          },
          {
            headerName: 'City',
            field: 'city',
            colId: 'city',
            minWidth: 200,
          },
          {
            headerName: 'State',
            field: 'state',
            colId: 'state',
            minWidth: 80,
            headerClass: 'state-header',
          },
          {
            headerName: 'Postal Code',
            field: 'zipCd',
            colId: 'zipCd',
            valueGetter: ({ data }) => {
              return data ? (data.zip4RestUs ? `${data.zip6}-${data.zip4RestUs}` : data.zip6) : '';
            },
            minWidth: 100,
          },
          {
            headerName: 'First Name',
            field: 'contactFirstName',
            colId: 'contactFirstName',
            minWidth: 200,
          },
          {
            headerName: 'Last Name',
            field: 'contactLastName',
            colId: 'contactLastName',
            minWidth: 200,
          },
          {
            headerName: 'Email',
            field: 'email',
            colId: 'email',
            minWidth: 250,
          },
          {
            headerName: 'Area Code',
            field: 'contactAreaCd',
            colId: 'contactAreaCd',
            minWidth: 80,
          },
          {
            headerName: 'Phone Number',
            field: 'contactPhone',
            colId: 'contactPhone',
            minWidth: 100,
          },
        ],
        keyField: 'index',
      }),
    ];
  }
}
